import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '../../../../../ui/modal/modal';
import styles from '../../../../assessment/create/create_assessment.module.css';

export type ModalOption = {
  value: string;
  label: string;
  modalContent: JSX.Element | ((closeModal: () => void) => React.ReactNode);
  disabled?: boolean;
  icon?: React.ReactNode; // Optional icon for each option
};

type DropdownWithModalProps = {
  options: ModalOption[];
  disabled?: boolean;
  label?: string;
  className?: string;
};

export const DropdownWithModal: React.FC<DropdownWithModalProps> = ({
  options,
  disabled = false,
  label = 'Generate Job Description',
  className = '',
}) => {
  const [selectedValue, setSelectedValue] = useState('');
  const [selectedOption, setSelectedOption] =
    useState<ModalOption | undefined>(undefined);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const resolveModalContent = () => {
    const foundOption = options.find((o) => o.value === selectedValue);

    if (foundOption) {
      return typeof foundOption.modalContent === 'function'
        ? foundOption.modalContent(closeModal)
        : foundOption.modalContent;
    } else {
      return <></>;
    }
  };

  const handleOptionSelect = (option: ModalOption) => {
    if (!option.disabled) {
      setSelectedValue(option.value);
      setSelectedOption(option);
      setIsModalVisible(true);
      setIsDropdownOpen(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      setIsModalVisible(false);
    };
  }, []);

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedValue('');
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      { /* Modern dropdown button */ }
      <button
          onClick={toggleDropdown}
          className={`
          flex items-center justify-between gap-2 w-full px-4 py-2.5 rounded-lg 
          font-medium text-sm transition-all duration-200
          ${
            disabled
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
              : 'bg-white text-green-600 shadow-sm hover:shadow border border-green-100 hover:border-green-200'
          }
        `}
          disabled={disabled}
      >
        <div className='flex items-center gap-2'>
          <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-5 w-5 text-green-500'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
            />
          </svg>
          <span>{ label }</span>
        </div>
        <svg
            className={`w-5 h-5 text-green-500 transform transition-transform duration-200 ${
              isDropdownOpen ? 'rotate-180' : ''
            }`}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
            xmlns='http://www.w3.org/2000/svg'
        >
          <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M19 9l-7 7-7-7'
          ></path>
        </svg>
      </button>

      { /* Dropdown Menu */ }
      { isDropdownOpen && (
        <div className='absolute z-50 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden'>
          { options.map((option) => (
            <button
                key={option.value}
                onClick={() => !option.disabled && handleOptionSelect(option)}
                className={`
                w-full px-4 py-3 text-sm text-left flex items-center gap-3 transition-colors
                ${
                  option.disabled
                    ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                    : 'text-gray-700 hover:bg-green-50 hover:text-green-700'
                }
                border-b border-gray-100 last:border-b-0
              `}
                disabled={option.disabled}
            >
              { option.icon || (
                <div className='w-6 h-6 flex items-center justify-center rounded-full bg-green-100'>
                  <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-3 w-3 text-green-600'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 6v6m0 0v6m0-6h6m-6 0H6'
                    />
                  </svg>
                </div>
              ) }
              { option.label }
            </button>
          )) }
        </div>
      ) }

      { /* Modal */ }
      { selectedOption && (
        <Modal
            visible={isModalVisible}
            toggle={closeModal}
            modalContent={() => resolveModalContent()}
            customClass={styles.modalDimensions}
            headerText={options.find((o) => o.value === selectedValue)?.label || ''}
        />
      ) }
    </div>
  );
};

export default DropdownWithModal;
